import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getHomeBundle, getHomeBelow, getHomeConfig } from "../lib/catalogueApi";
import { HomeHero } from "./HomeHero";
import { HomeCarousel } from "./HomeCarousel";
import { HomeTiles, TilesSkeleton } from "./HomeTiles";

// T61: navy-first default (the photo arrives with home-config); same box as the photo banner, so no shift
const DEFAULT_HERO = { bg: { kind: "navy" }, eyebrow: "Australian IT hardware, done right", headline: "Business technology, sourced and supported locally.",
  sub: "Laptops, monitors, networking, storage and servers for Australian workplaces. GST-inclusive pricing, no surprises.", cta_label: "Browse products", cta_link: "/products",
  cta2_label: "Build a procurement list", cta2_link: "/procurement", cta2_show: true, cta2_icon: true, note_show: false,
  features: [{ icon: "Tag", title: "GST-inclusive pricing", sub: "No hidden costs" }, { icon: "FileText", title: "PO on checkout", sub: "Add your own PO number" },
    { icon: "Layers", title: "Save & reorder", sub: "Reuse standard configs" }, { icon: "Headset", title: "Local support", sub: "Talk to a real person" }] };
const cfgP = getHomeConfig();
const CFG_KEY = "rs_home_cfg_v3";  // T65 fix: last-seen slides, so a repeat visit paints the same hero instantly (then reconciles)
const lastCfg = () => { try { return JSON.parse(localStorage.getItem(CFG_KEY)); } catch { return null; } };
// First-ever visit: a neutral navy box at the configured hero's height per breakpoint (no fallback copy, no height change)
const HeroSkeleton = () => <div aria-hidden="true" data-testid="hero-skeleton" className="h-[615px] w-full bg-[#08142D] md:h-[544px] lg:h-[595px] xl:h-[584px]" />;
import { SkeletonGrid } from "./Paging";
import { StoreLayout } from "./StoreLayout";
import { ProductCard } from "./ProductCard";
import { ClearanceHome } from "./ClearanceBits";
import { Truck, ArrowRight } from "lucide-react";
import { HomeHighlights } from "./HomeHighlights";
import { HomePromo } from "./HomePromo";
import { BrandTiles } from "./BrandTiles";

export default function Home() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [last] = useState(lastCfg);
  const keep = (c) => { setCfg(c); try { localStorage.setItem(CFG_KEY, JSON.stringify({ slides: (c.slides || [c.hero]).map((x) => ({ ...x, countdown: null })), carousel: c.carousel })); } catch { /* private mode */ } };
  useEffect(() => {
    const retry = () => new Promise((r) => setTimeout(r, 1500)).then(getHomeConfig);  // T59: one retry so a blip doesn't drop tiles + promo
    cfgP.catch(retry).then(keep).catch(() => setCfg({ tiles: null, hero: last?.slides?.[0] || DEFAULT_HERO, slides: last?.slides }));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  // T63: the schedule (and its countdown) just ended: fetch again so the default banner returns without a reload
  const ended = useCallback(() => { getHomeConfig(true).then(keep).catch(() => {}); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const shown = cfg ? (cfg.slides?.length > 1 ? cfg.slides : [cfg.hero]) : last?.slides?.map((x) => ({ ...x, pending: true }));
  useEffect(() => { getHomeBundle().then(setData).catch(() => setData({ featured_categories: [], featured_products: [] })); }, []);
  return (
    <StoreLayout>
      {!shown?.length ? <HeroSkeleton /> : shown.length > 1 ? <HomeCarousel slides={shown} settings={(cfg || last).carousel} onCountdownEnd={ended} /> : <HomeHero hero={shown[0]} onCountdownEnd={ended} />}

      {data && data.catalogue_empty && (
        <div className="max-w-6xl mx-auto px-5">
          <div data-testid="catalogue-empty-banner" className="my-12 rounded-2xl border border-[var(--brand-accent-tint)] bg-[#fffaf0] px-6 py-12 text-center">
            <Truck className="mx-auto text-[var(--brand-accent)]" size={34} />
            <h2 className="rs-h2 text-[var(--brand-primary)] mt-3">Catalogue update in progress</h2>
            <p className="text-slate-600 mt-2">{data.catalogue_empty_message || "Catalogue update in progress - new products landing shortly."}</p>
          </div>
        </div>
      )}

      {!data && (
        <div className="max-w-6xl mx-auto px-5 py-8 sm:py-12" data-testid="home-skeleton">
          <div className="mb-4 flex h-11 items-center sm:mb-5"><div className="h-6 w-48 bg-slate-100 rounded" /></div>
          <TilesSkeleton className="mb-12" pulse={false} />
          <SkeletonGrid n={8} cols="grid-cols-2 md:grid-cols-4" testid="home-featured-skeleton" />
        </div>
      )}

      {data && !data.catalogue_empty && (
        <div className="max-w-6xl mx-auto px-5">
          <section className="py-8 sm:py-12" data-testid="home-categories">
            <div className="mb-4 flex h-11 items-center justify-between gap-3 sm:mb-5">
              <h2 className="rs-h2 text-[var(--brand-primary)]">Shop by category</h2>
              <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("rs:open-menu"))} aria-haspopup="dialog" data-testid="home-browse-all"
                className="-mr-2 inline-flex min-h-11 items-center gap-1 rounded-lg px-2 text-sm font-semibold text-[var(--brand-link)] transition-colors hover:bg-slate-50 active:bg-slate-100 motion-reduce:transition-none">
                View all categories<ArrowRight size={16} aria-hidden="true" />
              </button>
            </div>
            {cfg?.tiles ? <HomeTiles tiles={cfg.tiles} /> : <TilesSkeleton />}
          </section>

          <section className="py-6" data-testid="home-featured">
            <h2 className="rs-h2 mb-5">Featured products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.featured_products.map((p, i) => <ProductCard key={p.id} p={p} priority={i < 2} buy />)}
            </div>
          </section>

          <BelowFold />

          <HomePromo promo={cfg?.promo} loading={!cfg} />
          <HomeHighlights items={cfg?.highlights} />
        </div>
      )}
    </StoreLayout>
  );
}

// T12.a: below-the-fold sections load only when scrolled near. T57: the placeholder reserves the measured loaded height per breakpoint (no layout shift)
function BelowFold() {
  const ref = useRef(null);
  const [below, setBelow] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let done = false;
    const load = () => { if (done) return; done = true; io.disconnect(); clearTimeout(t); getHomeBelow().then(setBelow).catch(() => setBelow({ featured_brands: [], new_arrivals: [] })); };
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) load(); }, { rootMargin: "400px" });
    const t = setTimeout(load, 1500);  // T58: also load shortly after first paint, so the sections render without scrolling (headless / no-scroll)
    io.observe(el);
    return () => { io.disconnect(); clearTimeout(t); };
  }, []);
  return (
    <div ref={ref} data-testid="home-below" className={below ? undefined : "min-h-[2360px] sm:min-h-[2575px] md:min-h-[1320px] lg:min-h-[1330px] xl:min-h-[1391px]"}>
      {below && below.new_arrivals?.length > 0 && (
        <section className="py-6" data-testid="home-new-arrivals">
          <h2 className="rs-h2 mb-5">New arrivals</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{below.new_arrivals.map((p) => <ProductCard key={p.id} p={p} buy />)}</div>
        </section>
      )}
      {below && <ClearanceHome />}
      {below && (
        <section className="py-12" data-testid="home-brands">
          <h2 className="rs-h2 mb-5">Trusted brands</h2>
          <BrandTiles brands={below.featured_brands} />
        </section>
      )}
    </div>
  );
}
